/**
 * Admin Order Management API
 * Swagger: http://13.126.29.66:5000/api#/OrderManagement/OrderManagementController_*
 * - fetchlistOfSP: GET /order-management/fetchlistOfSP/{order_id}
 * - assignSP: POST /order-management/assignSP
 * Base path is applied by apiClient (set REACT_APP_API_BASE_PATH=/api if your API is under /api).
 */

import apiClient from './apiClient';
import { assertDeployedApiSuccess } from './deployedContracts';
import { fetchKycOverview, type KycOverviewProvider } from './adminKycApi';
import { fixCaptainAssignEligibility } from '../utils/orderLocationUtils';

const ORDER_MANAGEMENT_BASE = '/order-management';

/** Order list item from GET /order-management */
export interface OrderManagementOrder {
  id: string;
  service_id?: string;
  order_user_id?: string;
  order_user_name?: string;
  order_user_phone?: string;
  order_user_email?: string;
  order_status: string;
  order_remarks?: string;
  order_address?: string;
  order_location?: { lat?: string; long?: string };
  order_breakup?: { item: string; price: number; quantity: number }[];
  service_provider_id?: string;
  createdAt?: string;
  [key: string]: unknown;
}

/** Service provider from GET /order-management/fetchlistOfSP/{order_id} */
export interface OrderManagementSP {
  id: string;
  service_provider_id: string;
  service_id?: string;
  skill_level?: string;
  skill_status?: string;
  service_provider_name?: string;
  service_provider_email?: string;
  service_provider_phone?: string;
  /** false = shown for visibility but cannot assign (no skill for this service). */
  can_assign?: boolean;
  assign_block_reason?: string | null;
  order_service_city?: string | null;
  captain_active_city?: string | null;
  captain_work_enabled?: boolean | null;
  work_status_label?: 'Online' | 'Offline' | 'Unknown';
  assign_warning?: string | null;
  [key: string]: unknown;
}

/** Payload for POST /order-management/assignSP */
export interface AssignSPPayload {
  order_id: string;
  service_provider_id: string;
  service_id: string;
}

export interface OrderListMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaginatedOrdersResult {
  items: OrderManagementOrder[];
  meta: OrderListMeta;
}

export interface FetchOrdersParams {
  page?: number;
  limit?: number;
  status?: string;
  assignment?: string;
  search?: string;
  order_user_id?: string;
}

function parseOrderListMeta(raw: unknown, fallbackCount: number): OrderListMeta {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const meta = (raw as { meta?: OrderListMeta }).meta;
    if (meta && typeof meta.total === 'number') {
      return {
        page: meta.page ?? 1,
        limit: meta.limit ?? fallbackCount,
        total: meta.total,
        totalPages: meta.totalPages ?? Math.max(1, Math.ceil(meta.total / (meta.limit || 1))),
      };
    }
    const items = (raw as { items?: unknown[] }).items;
    if (Array.isArray(items)) {
      return {
        page: 1,
        limit: items.length,
        total: items.length,
        totalPages: 1,
      };
    }
  }
  return {
    page: 1,
    limit: fallbackCount,
    total: fallbackCount,
    totalPages: 1,
  };
}

function parseOrderListPayload(body: unknown): { items: OrderManagementOrder[]; meta: OrderListMeta } {
  const envelope = body as { data?: unknown; success?: boolean; statusCode?: number };
  if (envelope?.success === false && typeof envelope?.statusCode === 'number' && envelope.statusCode >= 400) {
    return { items: [], meta: { page: 1, limit: 10, total: 0, totalPages: 1 } };
  }

  const data = envelope?.data ?? body;
  if (Array.isArray(data)) {
    return {
      items: data as OrderManagementOrder[],
      meta: {
        page: 1,
        limit: data.length,
        total: data.length,
        totalPages: 1,
      },
    };
  }

  if (data && typeof data === 'object') {
    const payload = data as { items?: OrderManagementOrder[]; meta?: OrderListMeta };
    const items = Array.isArray(payload.items) ? payload.items : [];
    return {
      items,
      meta: parseOrderListMeta(data, items.length),
    };
  }

  return { items: [], meta: { page: 1, limit: 10, total: 0, totalPages: 1 } };
}

/**
 * GET /order-management — DB pagination (page, limit) and optional filters.
 */
export async function fetchOrders(params?: FetchOrdersParams): Promise<PaginatedOrdersResult> {
  const res = await apiClient.get(ORDER_MANAGEMENT_BASE, {
    params: {
      page: params?.page ?? 1,
      limit: params?.limit ?? 10,
      ...(params?.status ? { status: params.status } : {}),
      ...(params?.assignment ? { assignment: params.assignment } : {}),
      ...(params?.search ? { search: params.search } : {}),
      ...(params?.order_user_id ? { order_user_id: params.order_user_id } : {}),
    },
  });
  const parsed = parseOrderListPayload(res.data);
  const enriched = await enrichOrdersWithCustomers(parsed.items);
  return {
    items: enriched,
    meta: parsed.meta,
  };
}

export function formatOrderPlacedBy(order: Pick<
  OrderManagementOrder,
  'order_user_name' | 'order_user_phone' | 'order_user_email' | 'order_user_id'
>): string {
  const name = String(order.order_user_name ?? '').trim();
  const phone = String(order.order_user_phone ?? '').trim();
  const email = String(order.order_user_email ?? '').trim();
  if (name) return name;
  if (phone) return phone;
  if (email) return email;
  const uid = String(order.order_user_id ?? '').trim();
  return uid ? `Customer ${uid.slice(0, 8)}` : 'Unknown customer';
}

export function formatOrderPlacedAt(order: OrderManagementOrder): string {
  const raw =
    order.createdAt ??
    order.created_at ??
    order.updatedAt ??
    order.updated_at;
  if (!raw) return '—';
  const d = new Date(String(raw));
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

async function enrichOrdersWithCustomers(orders: OrderManagementOrder[]): Promise<OrderManagementOrder[]> {
  const ids = Array.from(
    new Set(
      orders
        .filter((o) => !String(o.order_user_name ?? '').trim())
        .map((o) => String(o.order_user_id ?? '').trim())
        .filter(Boolean)
    )
  );
  if (ids.length === 0) return orders;

  const profileById = new Map<string, AssignedProviderProfile>();
  const batchSize = 8;
  for (let i = 0; i < ids.length; i += batchSize) {
    const batch = ids.slice(i, i + batchSize);
    const profiles = await Promise.all(batch.map((id) => fetchAssignedProviderProfile(id)));
    batch.forEach((id, idx) => {
      const p = profiles[idx];
      if (p) profileById.set(id, p);
    });
  }

  return orders.map((order) => {
    const uid = String(order.order_user_id ?? '').trim();
    if (!uid) return order;
    const profile = profileById.get(uid);
    if (!profile && String(order.order_user_name ?? '').trim()) return order;
    return {
      ...order,
      order_user_name: order.order_user_name?.trim() || profile?.name,
      order_user_phone: order.order_user_phone?.trim() || profile?.phone,
      order_user_email: order.order_user_email?.trim() || profile?.email,
    };
  });
}

function isSkillProviderRow(value: unknown): value is OrderManagementSP {
  if (!value || typeof value !== 'object') return false;
  const pid = String((value as OrderManagementSP).service_provider_id ?? '').trim();
  return pid.length > 0;
}

/** Collect every skill/captain row from nested API envelopes (never collapse a multi-item list to one). */
function extractListFromApiBody(body: unknown, depth = 0): OrderManagementSP[] {
  if (body == null || depth > 10) return [];

  if (Array.isArray(body)) {
    const rows: OrderManagementSP[] = [];
    for (const item of body) {
      if (isSkillProviderRow(item)) rows.push(item);
      else rows.push(...extractListFromApiBody(item, depth + 1));
    }
    return rows;
  }

  if (typeof body !== 'object') return [];

  const o = body as Record<string, unknown>;
  if (o.success === false || (typeof o.statusCode === 'number' && o.statusCode >= 400)) {
    return [];
  }

  if (Array.isArray(o.captains)) {
    return o.captains as OrderManagementSP[];
  }

  for (const key of ['data', 'skills', 'providers', 'items', 'list', 'results', 'captains']) {
    if (o[key] != null) {
      const nested = extractListFromApiBody(o[key], depth + 1);
      if (nested.length > 0) return nested;
    }
  }

  const values = Object.values(o);
  if (values.length > 1 && values.some((v) => isSkillProviderRow(v))) {
    return values.flatMap((v) =>
      isSkillProviderRow(v) ? [v] : extractListFromApiBody(v, depth + 1)
    );
  }

  if (isSkillProviderRow(body)) return [body as OrderManagementSP];
  return [];
}

function parseSpList(body: unknown): OrderManagementSP[] {
  return dedupeByProviderId(extractListFromApiBody(body));
}

export interface AssignSpListMeta {
  order_service_city?: string | null;
  order_city_resolved?: boolean;
  requirements?: string[];
  total_candidates_with_skill?: number;
  total_kyc_verified?: number;
  total_assignable?: number;
}

/** Parse GET fetchlistOfSP — supports legacy array or { captains, meta }. */
export function parseFetchListOfSpResponse(body: unknown): {
  captains: OrderManagementSP[];
  meta?: AssignSpListMeta;
} {
  if (body == null || typeof body !== 'object') {
    return { captains: [] };
  }
  const envelope = body as { data?: unknown; success?: boolean; statusCode?: number };
  if (envelope.success === false || (typeof envelope.statusCode === 'number' && envelope.statusCode >= 400)) {
    return { captains: [] };
  }

  const data = envelope.data ?? body;
  if (Array.isArray(data)) {
    return { captains: parseSpList(data) };
  }
  if (data && typeof data === 'object') {
    const payload = data as { captains?: unknown; meta?: AssignSpListMeta };
    if (Array.isArray(payload.captains)) {
      return {
        captains: dedupeByProviderId(payload.captains as OrderManagementSP[]),
        meta: payload.meta,
      };
    }
  }

  return { captains: parseSpList(body) };
}

function skillStatusRank(status?: string): number {
  const s = String(status ?? '').toUpperCase();
  if (s === 'APPROVED') return 3;
  if (s === 'PENDING') return 2;
  if (s === 'REJECTED') return 1;
  return 0;
}

function providerIsVerified(provider: KycOverviewProvider): boolean {
  const status = String(provider.verification_status ?? '').trim().toUpperCase();
  if (status === 'VERIFIED') return true;
  if (provider.total_items <= 0 || provider.total_pending > 0) return false;

  const docsApproved = provider.documents.every(
    (doc) => String(doc.document_status ?? '').trim().toUpperCase() === 'APPROVED'
  );
  const skillsApproved = provider.skills.every(
    (skill) => String(skill.skill_status ?? '').trim().toUpperCase() === 'APPROVED'
  );
  return docsApproved && skillsApproved;
}

async function fetchVerifiedKycProvidersForAssign(
  existing: OrderManagementSP[],
  serviceId?: string,
): Promise<OrderManagementSP[]> {
  try {
    const overview = await fetchKycOverview('ALL');
    const existingIds = new Set(
      existing.map((sp) => String(sp.service_provider_id ?? '').trim()).filter(Boolean)
    );

    return overview.providers
      .filter(providerIsVerified)
      .filter((provider) => !existingIds.has(String(provider.service_provider_id ?? '').trim()))
      .map((provider) => ({
        id: `kyc-${provider.service_provider_id}`,
        service_provider_id: provider.service_provider_id,
        service_id: serviceId,
        skill_status: undefined,
        skill_level: undefined,
        service_provider_name: provider.service_provider_name ?? undefined,
        service_provider_email: provider.service_provider_email ?? undefined,
        service_provider_phone: provider.service_provider_phone ?? undefined,
        provider_role: provider.provider_role ?? undefined,
        can_assign: false,
        assign_block_reason:
          'KYC is verified, but this captain does not have an approved skill for this order service.',
      }));
  } catch {
    return [];
  }
}

function dedupeByProviderId(list: OrderManagementSP[]): OrderManagementSP[] {
  const byPid = new Map<string, OrderManagementSP>();
  for (const sp of list) {
    const pid = String(sp.service_provider_id ?? '').trim();
    if (!pid) continue;
    const existing = byPid.get(pid);
    if (!existing) {
      byPid.set(pid, sp);
      continue;
    }
    const spAssignable = sp.can_assign !== false;
    const existingAssignable = existing.can_assign !== false;
    if (spAssignable && !existingAssignable) {
      byPid.set(pid, sp);
      continue;
    }
    if (!spAssignable && existingAssignable) continue;
    if (skillStatusRank(sp.skill_status) > skillStatusRank(existing.skill_status)) {
      byPid.set(pid, sp);
    }
  }
  return Array.from(byPid.values());
}

/** Assigned captain profile for order details (GET /users/:id). */
export interface AssignedProviderProfile {
  service_provider_id: string;
  name: string;
  email?: string;
  phone?: string;
  role_name?: string;
}

function parseAssignedProviderFromUserApi(
  body: unknown,
  providerId: string
): AssignedProviderProfile | null {
  if (!body || typeof body !== 'object') return null;
  const envelope = body as { data?: unknown; success?: boolean; statusCode?: number };
  if (envelope.success === false || (typeof envelope.statusCode === 'number' && envelope.statusCode >= 400)) {
    return null;
  }
  let row: Record<string, unknown> | null = null;
  if (Array.isArray(envelope.data) && envelope.data[0] && typeof envelope.data[0] === 'object') {
    row = envelope.data[0] as Record<string, unknown>;
  } else if (envelope.data && typeof envelope.data === 'object' && !Array.isArray(envelope.data)) {
    row = envelope.data as Record<string, unknown>;
  }
  if (!row) return null;

  const role = row.role as { role_name?: string } | undefined;
  const name = String(row.name ?? '').trim();
  const phone = String(row.phoneNumber ?? row.phone ?? '').trim();
  const emailRaw = row.email;
  const email =
    emailRaw != null && String(emailRaw).trim() ? String(emailRaw).trim() : undefined;

  return {
    service_provider_id: providerId,
    name: name || phone || `Provider ${providerId.slice(0, 8)}`,
    email,
    phone: phone || undefined,
    role_name: role?.role_name ? String(role.role_name) : undefined,
  };
}

/** GET /users/:id — same profile shape as KYC (data: [{ name, phoneNumber, role }]). */
export async function fetchAssignedProviderProfile(
  serviceProviderId: string
): Promise<AssignedProviderProfile | null> {
  try {
    const res = await apiClient.get(`/users/${encodeURIComponent(serviceProviderId)}`);
    return parseAssignedProviderFromUserApi(res.data, serviceProviderId);
  } catch {
    return null;
  }
}

async function fetchProviderUserProfile(providerId: string): Promise<Partial<OrderManagementSP> | null> {
  const profile = await fetchAssignedProviderProfile(providerId);
  if (!profile) return null;
  return {
    service_provider_id: profile.service_provider_id,
    service_provider_name: profile.name,
    service_provider_email: profile.email,
    service_provider_phone: profile.phone,
  };
}

async function enrichProvidersWithUserProfiles(list: OrderManagementSP[]): Promise<OrderManagementSP[]> {
  const ids = Array.from(
    new Set(
      list
        .filter(
          (sp) =>
            !String(sp.service_provider_name ?? '').trim() &&
            !String(sp.service_provider_phone ?? '').trim() &&
            !String(sp.service_provider_email ?? '').trim()
        )
        .map((sp) => String(sp.service_provider_id ?? '').trim())
        .filter(Boolean)
    )
  );
  if (ids.length === 0) return list;

  const profileById = new Map<string, Partial<OrderManagementSP>>();
  const batchSize = 8;
  for (let i = 0; i < ids.length; i += batchSize) {
    const batch = ids.slice(i, i + batchSize);
    const profiles = await Promise.all(batch.map((id) => fetchProviderUserProfile(id)));
    batch.forEach((id, idx) => {
      const p = profiles[idx];
      if (p) profileById.set(id, p);
    });
  }

  return list.map((sp) => {
    const pid = String(sp.service_provider_id ?? '').trim();
    const profile = pid ? profileById.get(pid) : undefined;
    if (!profile) return sp;
    return {
      ...sp,
      service_provider_name: sp.service_provider_name?.trim() || profile.service_provider_name,
      service_provider_email: sp.service_provider_email?.trim() || profile.service_provider_email,
      service_provider_phone: sp.service_provider_phone?.trim() || profile.service_provider_phone,
    };
  });
}

export async function fetchOrderById(orderId: string): Promise<OrderManagementOrder | null> {
  try {
    const res = await apiClient.get(`${ORDER_MANAGEMENT_BASE}/${encodeURIComponent(orderId)}`);
    const body = res.data as { data?: unknown; success?: boolean; statusCode?: number };
    if (body?.success === false || (typeof body?.statusCode === 'number' && body.statusCode >= 400)) {
      return null;
    }
    const raw = body?.data ?? body;
    let order: OrderManagementOrder | null = null;
    if (Array.isArray(raw) && raw[0] && typeof raw[0] === 'object') {
      order = raw[0] as OrderManagementOrder;
    } else if (raw && typeof raw === 'object' && 'id' in (raw as object)) {
      order = raw as OrderManagementOrder;
    }
    if (!order) return null;
    const [enriched] = await enrichOrdersWithCustomers([order]);
    return enriched ?? order;
  } catch {
    return null;
  }
}

async function fetchSkillsByStatus(status: string): Promise<OrderManagementSP[]> {
  const res = await apiClient.get('/service-provider-skills/fetchByStatus', {
    params: { status },
  });
  return parseSpList(res.data);
}

/**
 * All captains with APPROVED or PENDING skill for this service_id.
 * Uses GET /service-provider-skills/by-service/:id (one deduped row per captain).
 */
export async function fetchSkillsForService(serviceId: string): Promise<OrderManagementSP[]> {
  const sid = serviceId.trim();
  if (!sid) return [];

  try {
    const res = await apiClient.get(
      `/service-provider-skills/by-service/${encodeURIComponent(sid)}`
    );
    const list = parseSpList(res.data);
    if (list.length > 0) return list;
  } catch {
    // Fall back for older backends without by-service route
  }

  const statuses = ['APPROVED', 'PENDING'];
  const chunks = await Promise.all(
    statuses.map((status) => fetchSkillsByStatus(status).catch(() => [] as OrderManagementSP[]))
  );
  const forService = chunks
    .flat()
    .filter((s) => String(s.service_id ?? '').trim() === sid);
  return dedupeByProviderId(forService);
}

/**
 * GET /order-management/fetchlistOfSP/{order_id}
 * Returns captains with APPROVED skill for the order's service_id.
 */
export async function fetchListOfSP(orderId: string): Promise<OrderManagementSP[]> {
  const { captains } = await fetchListOfSPWithMeta(orderId);
  return captains;
}

export async function fetchListOfSPWithMeta(orderId: string): Promise<{
  captains: OrderManagementSP[];
  meta?: AssignSpListMeta;
}> {
  const url = `${ORDER_MANAGEMENT_BASE}/fetchlistOfSP/${encodeURIComponent(orderId)}`;
  const res = await apiClient.get(url);
  const parsed = parseFetchListOfSpResponse(res.data);
  if (parsed.captains.length > 0 || parsed.meta) {
    return parsed;
  }
  const body = res.data as { success?: boolean; statusCode?: number; message?: string };
  if (body?.success === false || (typeof body?.statusCode === 'number' && body.statusCode >= 400)) {
    throw new Error(
      typeof body.message === 'string' ? body.message : 'No service providers available for this order'
    );
  }
  return parsed;
}

/**
 * Assignable SPs for an order — single backend source (fetchlistOfSP).
 * Backend enforces: skill for service, work mode online, active city = order city.
 */
export async function fetchAssignableSPsForOrder(order: OrderManagementOrder): Promise<{
  providers: OrderManagementSP[];
  serviceId?: string;
  meta?: AssignSpListMeta;
  otherServiceSkillCount: number;
}> {
  const orderDetail = (await fetchOrderById(order.id)) ?? order;

  let captains: OrderManagementSP[] = [];
  let meta: AssignSpListMeta | undefined;
  try {
    const result = await fetchListOfSPWithMeta(order.id);
    captains = result.captains;
    meta = result.meta;
  } catch {
    captains = [];
  }

  const serviceId =
    String(orderDetail.service_id ?? order.service_id ?? '').trim() ||
    String(captains[0]?.service_id ?? '').trim() ||
    undefined;

  const extraVerified = await fetchVerifiedKycProvidersForAssign(captains, serviceId);
  const merged = dedupeByProviderId([...captains, ...extraVerified]);
  const providers = (await enrichProvidersWithUserProfiles(merged)).map((sp) =>
    fixCaptainAssignEligibility(sp),
  );

  providers.sort((a, b) => {
    const nameA = String(a.service_provider_name ?? '').toLowerCase();
    const nameB = String(b.service_provider_name ?? '').toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const skillCandidates = meta?.total_candidates_with_skill ?? 0;
  const totalKycVerified = meta?.total_kyc_verified ?? 0;
  const assignable = meta?.total_assignable ?? providers.length;

  return {
    providers,
    serviceId,
    meta: {
      ...meta,
      total_candidates_with_skill: skillCandidates,
      total_kyc_verified: totalKycVerified,
      total_assignable: assignable,
    },
    otherServiceSkillCount: Math.max(0, skillCandidates - assignable),
  };
}

/**
 * POST /order-management/assignSP
 * Assign a service provider to an order.
 */
export interface AssignSPResult {
  order_id: string;
  service_provider_id: string;
  order_status: string;
}

function isBrokenAssignSpFailure(message?: string): boolean {
  const m = String(message ?? '').toLowerCase();
  return (
    m.includes('service provider skill not assigned') ||
    m.includes('assign sp failed at') ||
    m.includes('cannot read propert') ||
    m.includes('order management not updated')
  );
}

/** Last resort if assignSP still returns legacy generic 400 on an old server. */
async function assignSPViaTransaction(payload: AssignSPPayload): Promise<AssignSPResult> {
  const txnRes = await apiClient.post('/order-transaction', {
    order_id: payload.order_id,
    service_provider_id: payload.service_provider_id,
    order_status: 'ASSIGNED',
  });
  assertDeployedApiSuccess(txnRes.data, 'Failed to create assignment transaction');

  const txnBody = txnRes.data as { data?: { service_provider_id?: string | null } };
  const savedSpId = String(
    txnBody?.data?.service_provider_id ?? payload.service_provider_id,
  ).trim();
  if (!savedSpId) {
    throw new Error(
      'Assignment transaction was created without service_provider_id. Deploy latest ots_be and use POST /order-management/assignSP.',
    );
  }

  try {
    await apiClient.patch(
      `${ORDER_MANAGEMENT_BASE}/${encodeURIComponent(payload.order_id)}`,
      {
        order_status: 'ASSIGNED',
        ...(payload.service_id ? { service_id: payload.service_id } : {}),
      },
    );
  } catch {
    /* optional on older servers */
  }

  return {
    order_id: payload.order_id,
    service_provider_id: savedSpId,
    order_status: 'ASSIGNED',
  };
}

function extractApiErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const body = (err as { response?: { data?: { message?: string } } }).response?.data;
    if (typeof body?.message === 'string' && body.message.trim()) {
      return body.message.trim();
    }
  }
  if (err instanceof Error) return err.message;
  return String(err ?? '');
}

function parseAssignSpSuccess(data: {
  data?: Partial<AssignSPResult> & Record<string, unknown>;
  order_id?: string;
  service_provider_id?: string;
  order_status?: string;
}, payload: AssignSPPayload): AssignSPResult {
  const result = data?.data ?? data;
  return {
    order_id: result?.order_id ?? payload.order_id,
    service_provider_id: result?.service_provider_id ?? payload.service_provider_id,
    order_status: result?.order_status ?? 'ASSIGNED',
  };
}

export async function assignSP(payload: AssignSPPayload): Promise<AssignSPResult> {
  try {
    const res = await apiClient.post(`${ORDER_MANAGEMENT_BASE}/assignSP`, payload);
    const data = res.data as {
      success?: boolean;
      statusCode?: number;
      message?: string;
      error?: unknown;
      data?: Partial<AssignSPResult> & Record<string, unknown>;
      order_id?: string;
      service_provider_id?: string;
      order_status?: string;
    };
    if (data?.success === false || (typeof data?.statusCode === 'number' && data.statusCode >= 400)) {
      throw new Error(data?.message || 'Failed to assign service provider');
    }
    return parseAssignSpSuccess(data, payload);
  } catch (err) {
    const apiMessage = extractApiErrorMessage(err);
    if (!isBrokenAssignSpFailure(apiMessage)) {
      throw err instanceof Error ? err : new Error(apiMessage || 'Failed to assign service provider');
    }
    try {
      return await assignSPViaTransaction(payload);
    } catch (fallbackErr) {
      const fbMsg = extractApiErrorMessage(fallbackErr);
      throw new Error(
        `${apiMessage}. Transaction assign also failed: ${fbMsg}. Deploy latest ots_be to EC2 (SSH key required).`,
      );
    }
  }
}

/** Cleared shape for PATCH — keeps order row; removes price, items, address, location, remarks. */
export const CLEARED_ORDER_DETAILS = {
  order_breakup: [] as { item: string; price: number; quantity: number }[],
  order_address: '',
  order_remarks: '',
  order_location: { lat: '', long: '' },
};

/**
 * PATCH /order-management/:id
 * Clears pricing and customer-facing details only (does not remove the order).
 */
export async function clearOrderDetails(orderId: string): Promise<void> {
  const res = await apiClient.patch(
    `${ORDER_MANAGEMENT_BASE}/${encodeURIComponent(orderId)}`,
    CLEARED_ORDER_DETAILS
  );
  assertDeployedApiSuccess(res.data, 'Failed to clear order details');
}

/**
 * POST /order-management/:id/cancel
 * Admin cancel (force) — allowed until terminal status.
 */
export async function cancelOrderAsAdmin(
  orderId: string,
  reason?: string
): Promise<void> {
  const res = await apiClient.post(
    `${ORDER_MANAGEMENT_BASE}/${encodeURIComponent(orderId)}/cancel`,
    { reason, force: true }
  );
  assertDeployedApiSuccess(res.data, 'Failed to cancel order');
}
